import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Link,
  Text,
  Tooltip,
  useDisclosure,
  usePrevious,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, Switch } from '@gamma/ui/form-fields';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import ErrorSharpIcon from '@gamma/ui/icons/ErrorSharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { isEqual } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { URLS } from '../../../../shared/constants';
import { useLogOperationCall } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import {
  GET_EDR_CONFIGURATION,
  IVerifyEDRConfiguration,
  useDeleteEDRConfiguration,
  useGetEDRConfiguration,
  useGetEDRConfigurationPublic,
  useStoreEDRConfiguration,
  useVerifyEDRConfiguration,
} from '../../../entities/queries';

const { integrations } = i18n.pages;
const { save, cancel, error, success, warning } = integrations;

const {
  noGoBack,
  yesDelete,
  enabled: enabledLabel,
  disabled: disabledLabel,
  paloAlto,
  integration,
  connectionVerified: connectionVerifiedMessage,
} = integrations;

const {
  integrationHeader,
  accessKey,
  accessKeyTooltip,
  deleteConnectionHeader,
  deleteConnectionTagline,
  secretKey,
  secretKeyTooltip,
  s3Bucket,
  s3BucketTooltip,
  s3FilePrefix,
  s3FilePrefixTooltip,
  s3Region,
  verifyPaloAltoConnection,
  documentation,
  forMoreInformation,
  saveSuccess,
  saveError,
  verifyError,
  deleteError,
  deleteSuccess,
  verifyConnection,
  connectionNotVerified,
} = paloAlto;

const { PALO_ALTO_DOCS } = URLS;

interface PaloAltoSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PaloAltoSettingsModal = ({
  isOpen,
  onClose,
}: PaloAltoSettingsModalProps) => {
  const [configurationVerified, setConfigurationVerified] = useState<
    boolean | undefined
  >();
  const [clientSecretInputPwType, setClientSecretInputPwType] =
    useState<boolean>(true);
  const [clientAccessInputPwType, setClientAccessInputPwType] =
    useState<boolean>(true);
  const [formIsReady, setFormIsReady] = useState<boolean>(false);
  const showToast = useToast();
  const { userRole, setUserPrivileges, userPrivileges, userLicense } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const getEDRConfigQuery =
    userRole === 'admin'
      ? useGetEDRConfiguration
      : useGetEDRConfigurationPublic;

  const {
    loading: EDRConfigLoading,
    error: EDRConfigError,
    data: EDRData,
  } = getEDRConfigQuery({
    variables: {
      provider_name: 'palo_alto_networks',
    },
    skip: !isOpen,
    onCompleted: (data) => {
      if (data.getEDRConfiguration === null) {
        reset({});
        setFormIsReady(true);
      } else {
        const { provider_name: _, ...rest } = data.getEDRConfiguration;
        reset({ ...rest });
        setFormIsReady(true);
      }
    },
  });

  const client = useApolloClient();

  const logOperationCall = useLogOperationCall();

  const {
    getValues,
    watch,
    control,
    register,
    reset,
    formState: { isValid },
  } = useForm();

  const formData = watch();

  const previousFormData = usePrevious(formData);

  useEffect(() => {
    if (
      previousFormData !== undefined &&
      !isEqual(previousFormData, formData)
    ) {
      setConfigurationVerified(undefined);
    }
  }, [formData, previousFormData]);

  const {
    isOpen: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose,
  } = useDisclosure();

  const [verifyEDRConfiguration, { loading: verifyEDRConfigurationLoading }] =
    useVerifyEDRConfiguration({
      fetchPolicy: 'network-only',
      onCompleted: (data: IVerifyEDRConfiguration) => {
        setConfigurationVerified(data?.verifyEDRConfiguration?.verified);
      },
      onError: () => {
        showToast({
          title: error,
          description: verifyError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [storeEDRConfiguration, { loading: storeEDRConfigurationLoading }] =
    useStoreEDRConfiguration({
      variables: {
        provider_name: 'palo_alto_networks',
        config: {
          palo_alto_networks: {
            ...formData,
            polling_seconds: -1,
            edr_action_enabled: formData.enabled,
          },
        },
      },
      onCompleted: async () => {
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'getEDRConfiguration',
        });
        client.cache.gc();
        showToast({
          title: success,
          description: saveSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const updatedInput = {
          status: formData.enabled ? 'Enabled' : 'Disabled',
          service_name: 'Palo Alto Networks',
        };
        logOperationCall(
          'ENABLE_DISABLE_CONFIGURATION',
          'INTEGRATION',
          JSON.stringify(updatedInput),
        );
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? '']['palo_alto_networks'] =
          formData.enabled;
        setUserPrivileges(newUserPrivileges);
        handleClose();
      },
      onError: async () => {
        showToast({
          title: error,
          description: saveError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [deleteEDRConfiguration, { loading: deleteEDRConfigurationLoading }] =
    useDeleteEDRConfiguration({
      variables: {
        provider_name: 'palo_alto_networks',
      },
      refetchQueries: [
        {
          query: GET_EDR_CONFIGURATION,
          variables: {
            provider_name: 'palo_alto_networks',
          },
        },
      ],
      onCompleted: async () => {
        showToast({
          title: success,
          description: deleteSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? '']['palo_alto_networks'] = false;
        setUserPrivileges(newUserPrivileges);
        reset({ enabled: false });
        onClose();
      },
      onError: async () => {
        showToast({
          title: error,
          description: deleteError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const handleClose = () => {
    if (EDRData?.getEDRConfiguration === null) {
      reset({ enabled: false });
    } else if (EDRData?.getEDRConfiguration) {
      const { provider_name: _, ...rest } = EDRData.getEDRConfiguration;
      reset({ ...rest });
    }
    onClose();
  };

  return EDRConfigError || EDRConfigLoading || !formIsReady ? (
    <GraphQLReqStatus
      error={EDRConfigError}
      isBackground
      loading={EDRConfigLoading || !formIsReady}
    ></GraphQLReqStatus>
  ) : (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={handleClose}
        isCentered={true}
        title={`Palo Alto ${integration}`}
        closeOnEsc={!verifyEDRConfigurationLoading}
        isCloseDisabled={verifyEDRConfigurationLoading}
        closeOnOverlayClick={!verifyEDRConfigurationLoading}
        body={
          <VStack as="form" alignItems="flex-start" spacing={4}>
            <Text textStyle="body-md">{integrationHeader}</Text>
            <Controller
              name="enabled"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Switch
                  {...register('enabled', {})}
                  inline
                  name={name}
                  isChecked={value}
                  isDisabled={false}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  label={formData.enabled ? enabledLabel : disabledLabel}
                />
              )}
            />
            <Box>
              <Text>
                {verifyPaloAltoConnection}
                {forMoreInformation}
                <Link
                  href={PALO_ALTO_DOCS}
                  textDecoration="underline"
                  isExternal
                >
                  {documentation}
                </Link>
                .
              </Text>
            </Box>
            <Input
              {...register('access_key', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              type={clientAccessInputPwType ? 'password' : 'text'}
              label={accessKey}
              tooltip={accessKeyTooltip}
              rightElement={
                <IconButton
                  cursor="pointer"
                  aria-label={`${
                    clientAccessInputPwType ? 'Show' : 'Hide'
                  } Client Access Key`}
                  onClick={() =>
                    setClientAccessInputPwType(!clientAccessInputPwType)
                  }
                >
                  {clientAccessInputPwType ? (
                    <VisibilitySharpIcon size="sm" />
                  ) : (
                    <VisibilityOffSharpIcon size="sm" />
                  )}
                </IconButton>
              }
            />
            <Input
              {...register('secret_key', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={secretKey}
              tooltip={secretKeyTooltip}
              type={clientSecretInputPwType ? 'password' : 'text'}
              rightElement={
                <IconButton
                  cursor="pointer"
                  aria-label={`${
                    clientSecretInputPwType ? 'Show' : 'Hide'
                  } Client Secret`}
                  onClick={() =>
                    setClientSecretInputPwType(!clientSecretInputPwType)
                  }
                >
                  {clientSecretInputPwType ? (
                    <VisibilitySharpIcon size="sm" />
                  ) : (
                    <VisibilityOffSharpIcon size="sm" />
                  )}
                </IconButton>
              }
            />

            <Input
              {...register('bucket', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={s3Bucket}
              tooltip={s3BucketTooltip}
            />

            <Input
              {...register('prefix', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={s3FilePrefix}
              tooltip={s3FilePrefixTooltip}
            />
            <Input
              {...register('region', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={s3Region}
            />
            <ButtonGroup
              w="100%"
              justifyContent="space-between"
              isDisabled={!isValid || configurationVerified}
            >
              <Button
                variant="solid"
                colorScheme="gray"
                leftIcon={<CheckCircleSharpIcon />}
                onClick={() => {
                  verifyEDRConfiguration({
                    variables: {
                      provider_name: 'palo_alto_networks',
                      config: {
                        palo_alto_networks: {
                          ...getValues(),
                          polling_seconds: -1,
                          edr_action_enabled: formData.enabled,
                        },
                      },
                    },
                  });
                }}
                isDisabled={!isValid || configurationVerified}
                isLoading={verifyEDRConfigurationLoading}
              >
                {verifyConnection}
              </Button>
              <Tooltip label={deleteConnectionHeader}>
                <IconButton
                  variant="solid"
                  colorScheme={deleteEDRConfigurationLoading ? 'gray' : 'red'}
                  onClick={deleteOnOpen}
                  icon={<DeleteSharpIcon />}
                  aria-label={deleteConnectionHeader}
                  isLoading={deleteEDRConfigurationLoading}
                  isDisabled={deleteEDRConfigurationLoading || !isValid}
                />
              </Tooltip>
            </ButtonGroup>
            {configurationVerified === true && (
              <Alert status="success" w="100%">
                <Flex>
                  <CheckCircleSharpIcon color="green.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionVerifiedMessage}
                    </Text>
                  </Box>
                </Flex>
              </Alert>
            )}
            {configurationVerified === false && (
              <Alert status="error" w="100%">
                <Flex>
                  <WarningSharpIcon color="red.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionNotVerified}
                    </Text>

                    <Text>{verifyError}</Text>
                  </Box>
                </Flex>
              </Alert>
            )}
          </VStack>
        }
        footer={
          <ButtonGroup isDisabled={storeEDRConfigurationLoading}>
            <Button variant="solid" colorScheme="gray" onClick={handleClose}>
              {cancel}
            </Button>
            <Button
              variant="solid"
              onClick={() => {
                storeEDRConfiguration();
              }}
              isLoading={storeEDRConfigurationLoading}
              isDisabled={
                storeEDRConfigurationLoading ||
                !isValid ||
                (formData?.enabled && !configurationVerified)
              }
            >
              {save}
            </Button>
          </ButtonGroup>
        }
      />
      <Modal
        size="lg"
        isOpen={deleteIsOpen}
        onClose={deleteOnClose}
        title={deleteConnectionHeader}
        body={
          <Alert variant="subtle" status="warning" w="100%">
            <Flex>
              <ErrorSharpIcon color="yellow.100" mr={2} />
              <Box>
                <Text textStyle="body-md-bold">{warning}</Text>
                <Text>{deleteConnectionTagline}</Text>
              </Box>
            </Flex>
          </Alert>
        }
        footer={
          <ButtonGroup isDisabled={deleteEDRConfigurationLoading}>
            <Button variant="solid" colorScheme="gray" onClick={deleteOnClose}>
              {noGoBack}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                deleteEDRConfiguration();
                deleteOnClose();
              }}
              isLoading={deleteEDRConfigurationLoading}
              isDisabled={deleteEDRConfigurationLoading}
            >
              {yesDelete}
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};
